import type { Translation } from '../types';

export const zhHant: Translation = {
  meta: { name: '繁體中文', english: 'Chinese (Traditional)', tag: 'zh-Hant', dir: 'ltr' },

  common: {
    menu: "選單",
    cancel: '取消',
    done: '完成',
    join: '加入',
    leave: '離開',
    refresh: '重新整理',
    save: '儲存',
    you: '你',
    host: '房主',
    ai: 'AI',
    ready: '已準備',
    notReady: '未準備',
  },

  conn: {
    live: '已連線',
    connecting: '連線中',
    offline: '已斷線',
    connectingFull: '連線中…',
    reconnectBanner: '連線中斷，正在重新連線。你的座位會保留 3 分鐘。',
  },

  nickname: {
    pickTitle: '取個名字',
    changeTitle: '修改名字',
    subtitle: '其他玩家會看到這個名字。',
    placeholder: '例如：萬用 +4',
    label: '暱稱',
    letsPlay: '開始遊戲',
  },

  lobby: {
    heroTitle: '和朋友一起玩 UNO',
    heroSubtitle: '免註冊，分享連結就能開局。',
    createRoom: '建立房間',
    roomCode: '房間代碼',
    publicRooms: '公開房間',
    empty: '目前還沒有公開房間，來建立第一個吧。',
    cardMeta: '房主 {host} · 每回合 {seconds} 秒',
    stacking: '可疊加',
    badgeOpen: '可加入',
    badgeFull: '已滿',
    badgeLive: '遊戲中',
    lockedTitle: '房間 {id} 已上鎖',
    password: '密碼',
    passwordLabel: '房間密碼',
  },

  create: {
    title: '建立房間',
    roomName: '房間名稱',
    roomNamePlaceholder: '週五夜 UNO',
    maxPlayers: '人數上限',
    playersOption: { other: '{count} 人' },
    passwordOptional: '密碼（選填）',
    passwordPlaceholder: '留空則任何人都能加入',
    listPublic: '顯示在公開房間清單中',
    houseRules: '自訂規則',
    submit: '建立',
  },

  rules: {
    turnTimer: '回合時限',
    turnTimerHint: '超時後，機器人會替你隨機打出一張合法的牌。',
    startingHand: '起始手牌',
    unoPenalty: 'UNO 罰牌',
    stackDrawTwo: '+2 疊加',
    stackDrawTwoHint: '可用 +2 回應 +2，不必抽牌',
    stackDrawFour: '+4 疊加',
    stackDrawFourHint: '可用 +4 回應 +4',
    challengeWildFour: '+4 質疑',
    challengeWildFourHint: '下一位玩家可以質疑不合法的 +4',
    strictWildFour: '嚴格 +4',
    strictWildFourHint: '只有手中沒有當前顏色的牌時才能打出',
    playDrawn: '抽到即可打出',
    playDrawnHint: '抽到的牌可以立刻打出',
    drawUntilPlayable: '抽到能出為止',
    drawUntilPlayableHint: '持續抽牌直到抽到可以打出的牌',
    hostOnly: '只有房主可以修改',
  },

  room: {
    copyLink: '複製邀請連結',
    copied: '✓ 已複製',
    roomLabel: '房間',
    locked: '已上鎖',
    public: '公開',
    unlisted: '不公開',
    readyStatus: '{total} 人中已有 {ready} 人準備',
    botsJoining: { other: '{count} 個機器人將加入' },
    startsWhenReady: '所有人準備好後立刻開始。',
    needMorePlayers: '至少需要 {min} 人才能開始，請邀請朋友或加入機器人。',
    playersTitle: '玩家',
    botsTitle: 'AI 機器人',
    botsHostOnly: '只有房主可以修改',
    noBots: '沒有機器人',
    botsWillJoin: { other: '{count} 個機器人將加入' },
    botsHint: '遊戲開始時入座。目前最多可容納 {max} 個。',
    botsRoomFull: '房間已滿，無法加入機器人。',
    soloCta: '想自己玩？加入機器人即可開始',
    addBot: '加入機器人',
    removeBot: '減少機器人',
    removePlayer: '移除 {name}',
    botPlaceholder: '機器人 {n}',
    defaultRoomName: '{name} 的房間',
    imReady: '我準備好了',
    cancelReady: '取消準備',
    offlineTag: '離線',
  },

  game: {
    yourTurn: '輪到你了',
    yourTurnStack: '輪到你了 — 疊加或抽 {count} 張',
    othersTurn: '輪到 {name}',
    waiting: '等待中…',
    gameOver: '遊戲結束',
    challengeWindow: '萬用 +4 — 質疑時間',
    drawPileLeft: '剩餘 {count} 張',
    discard: '棄牌堆',
    draw: '抽牌',
    takeCards: '抽 {count} 張',
    pass: '過牌',
    uno: 'UNO！',
    judge: '質疑',
    cardCount: { other: '{count} 張牌' },
    clockwise: '順時針',
    counterClockwise: '逆時針',
    activeColour: '目前顏色',
    noCards: '手牌出完了 — 漂亮。',
    oneCard: '只剩一張！',
    unoTag: 'UNO',
    forfeitedTag: '已退出',
    offlineFor: '離線 {seconds} 秒',
    placeTag: '第 {place} 名',
    pickColour: '選擇顏色',
    challengeTitle: '{name} 打出了萬用 +4，要質疑嗎？',
    challengeExplain: '若對方虛張聲勢，改由對方抽 {bluff} 張；否則你要抽 {fail} 張。',
    challengeBtn: '質疑',
    acceptBtn: '抽 {count} 張',
    youWin: '🎉 你贏了！',
    standingsTitle: '遊戲結束',
    points: '{points} 分',
    forfeited: '已退出',
    backToLobby: '返回大廳',
    playAgain: '再玩一局',
    waitingOthers: '等待其他玩家…',
    faceDownCard: '背面朝上的牌',
    sortByColour: "依顏色排序",
    sortAsDealt: "保持發牌順序",
    drawACard: '抽一張牌',
    drawNCards: '抽 {count} 張牌',
  },

  events: {
    someone: '有人',
    unoCalledYou: '你喊了 UNO！',
    unoCalledOther: '{name} 喊了 UNO！',
    judged: '{accuser} 抓到了 {target} — 罰 {count} 張',
    judgedYou: '{accuser} 抓到了你 — 罰 {count} 張',
    challengeBluff: '虛張聲勢被識破 — {name} 抽 {count} 張',
    challengeFailed: '質疑失敗 — {name} 抽 {count} 張',
    forfeited: '{name} 已退出。',
    afkYou: '你超時了 — 已自動出牌。',
    afkOther: '{name} 超時了 — 已自動出牌。',
    hostChanged: '{name} 成為新房主。',
    reshuffled: '抽牌堆已重新洗牌。',
    youWin: '你贏了！',
    winner: '{name} 獲勝！',
    gameOver: '遊戲結束。',
  },

  errors: {
    bad_request: '出了點問題。',
    no_session: '連線階段已過期，正在重新連線。',
    room_not_found: '該房間不存在。',
    room_full: '該房間已滿。',
    wrong_password: '密碼錯誤。',
    already_in_room: '請先離開目前的房間。',
    not_in_room: '你不在任何房間中。',
    not_host: '只有房主可以操作。',
    game_in_progress: '該遊戲已經開始。',
    not_enough_players: '人數不足。',
    stale_action: '其他玩家先一步行動，你的操作已被取消。',
    illegal_action: '這個操作不被允許。',
    rate_limited: '操作太快了。',
    nickname_invalid: '這個暱稱無法使用。',
  },

  theme: {
    title: '卡牌主題',
    description: '缺少的圖片會自動回退到內建的 CSS 卡面。',
    builtIn: '內建 — 不需要任何圖片',
    pngHint: '把 300×450 的 PNG 放進 {path}，然後在主題索引中註冊。',
  },

  language: {
    title: '語言',
    description: '立即生效，並會記住在這台裝置上的選擇。',
  },

  cards: {
    red: "紅",
    yellow: "黃",
    green: "綠",
    blue: "藍",
    wild: "萬用",
    skip: "跳過",
    reverse: "迴轉",
    draw2: "+2",
    wildCard: "萬用",
    wild4: "+4",
  },

  log: {
    title: "對局紀錄",
    empty: "目前沒有紀錄。",
    close: "隱藏紀錄",
    timestamps: "顯示時間",
    chatPlaceholder: "說點什麼…",
    send: "發送",
    played: "{name} 打出了 {card}",
    drew: "{name} 抽了 {count} 張",
    skipped: "{name} 被跳過",
    reversed: "方向已反轉",
    joined: "{name} 加入了",
    left: "{name} 離開了",
    started: "遊戲開始",
  },

  profile: {
    avatar: "頭像",
    avatarHint: "選一個頭像，或保留首字母。",
  },

  leave: {
    title: "要離開牌局嗎？",
    body: "這一局還在進行中。離開將視為退出，你的手牌會放回牌堆。",
    stay: "繼續遊戲",
    confirm: "仍要離開",
    unload: "你正在牌局中。",
  },
};
